import React, { useEffect, useState } from "react";
import axios from "axios";
import DefaultLayout from "../layout/DefaultLayout";
import Loading from "../components/Loading";
import Alert from "../components/Alert";
import { FaMapMarkerAlt, FaPhone, FaLink } from "react-icons/fa";
import colegioService from "../services/ColegioService";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumbs/Breadcrumb";

type RawColegio = Record<string, any>;
const Url = process.env.REACT_APP_API_URL;

type Colegio = {
    id_col: string;
    col_id: string;
    col_nom: string;
    col_dir: string;
    col_nom_pte: string;
    coordenadas: string;
    dom_te: string;
    sitioWeb: string;
};


const ColegiosDepartamentales: React.FC = () => {
    const [colegios, setColegios] = useState<Colegio[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>(undefined);
    const [showPhoneModal, setShowPhoneModal] = useState<boolean>(false);
    const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            setError(undefined);
            try {
                const response = await colegioService.obtenerColegiosRegionales();

                if (response.data.success && response.data.data)
                    setColegios(response.data.data);
                else
                    setError(response?.errors?.[0] ?? "Error al obtener colegios");
            } catch (ex: any) {
                setError(ex?.response?.data?.errors?.[0] ?? ex?.message ?? "Ocurrió un error al cargar colegios");
            } finally {
                setLoading(false);
            }
        };

        fetch();
    }, []);

    const openMaps = (coordenadas?: string) => {
        if (!coordenadas) return;
        const [lat, lon] = coordenadas.split(",").map((c) => c.trim());
        const url = `https://www.google.com/maps?q=${lat},${lon}`;
        window.open(url, "_blank", "noopener");
    };

    const makeCall = (telefono?: string) => {
        if (!telefono) return;
        window.location.href = `tel:${telefono}`;
    };

    const openWebsite = (url?: string) => {
        if (!url) return;
        const ok = url.startsWith("http") ? url : `https://${url}`;
        window.open(ok, "_blank", "noopener");
    };

    const openPhoneModal = (telefono?: string) => {
        setSelectedPhone(telefono ?? "No disponible");
        setShowPhoneModal(true);
    };

    const closePhoneModal = () => {
        setShowPhoneModal(false);
        setSelectedPhone(null);
    };

    return (
        <DefaultLayout>

            <Breadcrumb pageName="Colegios departamentales" />

            <div className="p-2">
                <div className="max-w-6xl mx-auto">
                    {loading && <Loading statusText="Cargando colegios..." />}

                    {error && <Alert type="failure" message={error} />}

                    {!loading && !error && colegios.length === 0 && (
                        <div className="bg-white rounded p-6 text-center text-gray-600">No se encontraron colegios</div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {colegios.map((c) => (
                            <div key={c.col_id} className="bg-white rounded-lg shadow p-6 flex flex-col md:flex-row items-center">
                                <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                                    {c.col_id ? (
                                        // logos are served from public/logos/{code}.jpg
                                        // fallback to empty box if image not found
                                        <img
                                            src={`${Url}/assets/logos/${c.col_id}.jpg`}
                                            alt={c.col_nom}
                                            className="w-20 h-20 object-contain"
                                            onError={(e) => {
                                                // hide broken image
                                                (e.target as HTMLImageElement).style.display = "none";
                                            }}
                                        />
                                    ) : (
                                        <div className="w-20 h-20 bg-gray-100 rounded" />
                                    )}
                                </div>

                                <div className="flex-1 px-4 text-center md:text-left">
                                    <div className="text-sm font-bold text-gray-800">{c.col_nom}</div>
                                    {c.col_dir ? <div className="text-sm text-gray-500 mt-1">{c.col_dir}</div> : null}
                                    {c.col_nom_pte ? <div className="text-sm text-gray-600 mt-1">Presidente: {c.col_nom_pte}</div> : null}
                                </div>

                                <div className="flex gap-6 mt-4 md:mt-0 md:ml-4">
                                    <button
                                        onClick={() => openMaps(c.coordenadas)}
                                        className="flex flex-col items-center text-xs text-primario-celeste-800 hover:text-primario-celeste-900"
                                        title="Abrir en Maps"
                                    >
                                        <FaMapMarkerAlt size={20} />
                                        <span className="mt-1">DIRECCIÓN</span>
                                    </button>
                                    {/* Boton para Mobile*/}
                                    <button
                                        onClick={() => makeCall(c.dom_te)}
                                        className="md:hidden flex flex-col items-center text-xs text-primario-celeste-800 hover:text-primario-celeste-900"
                                        title={c.dom_te ?? "Llamar"}
                                        aria-label={c.dom_te ? `Llamar ${c.dom_te}` : "Llamar"}
                                    >
                                        <FaPhone size={20} />
                                        <span className="mt-1">TELÉFONO</span>
                                    </button>
                                    {/* Boton para escritorio*/}
                                    <button
                                        onClick={() => openPhoneModal(c.dom_te)}
                                        className="hidden md:flex flex-col items-center text-xs text-primario-celeste-800 hover:text-primario-celeste-900"
                                        title={c.dom_te ?? "Llamar"}
                                        aria-label={c.dom_te ? `Ver teléfono ${c.dom_te}` : "Ver teléfono"}
                                    >
                                        <FaPhone size={20} />
                                        <span className="mt-1">TELÉFONO</span>
                                    </button>

                                    <button
                                        onClick={() => openWebsite(c.sitioWeb)}
                                        className="flex flex-col items-center text-xs text-primario-celeste-800 hover:text-primario-celeste-900"
                                        title="Sitio web"
                                    >
                                        <FaLink size={20} />
                                        <span className="mt-1">SITIO WEB</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {showPhoneModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                            <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
                                <div className="bg-primario-celeste-800 px-4 py-3">
                                    <h3 className="text-center text-base font-semibold text-white">Teléfono de Contacto</h3>
                                </div>

                                <div className="p-5">
                                    <div className="flex items-center justify-center gap-3 text-lg font-semibold text-gray-800 text-center">
                                        <FaPhone className="text-primario-celeste-800" size={22} />
                                        <span>{selectedPhone ?? "-"}</span>
                                    </div>

                                    <div className="mt-6 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={closePhoneModal}
                                            className="rounded bg-primario-celeste-800 px-6 py-2 font-semibold text-white"
                                        >
                                            Volver
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 justify-center mt-8">
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                        >
                            Volver
                        </button>
                    </div>
                </div>
            </div>
        </DefaultLayout>
    );
};

export default ColegiosDepartamentales;